import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapView.css";

/*
  MapView.jsx — Leaflet map with vessel legend, toggle, fly-to, and timeline scrubber

  Features:
  - Each vessel gets its own colored track
  - Legend shows vessel name, MMSI, color, and record count
  - Click legend item to fly to that vessel + activate timeline scrubber
  - Toggle button per vessel to show/hide their track
  - Timeline scrubber: drag to move a position marker along the focused vessel's track
    Full track stays visible — scrubber adds a moving indicator on top
*/

// Fix Leaflet's default icon paths which break in React/webpack builds
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Colors for up to 5 vessels — matches paid tier max
const VESSEL_COLORS = ["#2563eb", "#dc2626", "#16a34a", "#d97706", "#7c3aed"];

export default function MapView({ records, mode }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // One layer group per vessel MMSI — lets us show/hide independently
  const vesselLayersRef = useRef(new Map());

  // Bounds per vessel — for fly-to on legend click
  const vesselBoundsRef = useRef(new Map());

  // Full sorted records per vessel — needed for scrubber
  const vesselRecordsRef = useRef(new Map());

  // Scrubber marker — single Leaflet circleMarker that moves as user scrubs
  const scrubMarkerRef = useRef(null);

  // Visibility state per vessel — drives the toggle button UI
  const [visibility, setVisibility] = useState({});

  // Vessel metadata for the legend — built when records change
  const [vessels, setVessels] = useState([]);

  // Timeline scrubber state
  const [focusedMmsi, setFocusedMmsi] = useState(null);
  const [scrubIndex, setScrubIndex] = useState(0);

  // Initialize map once on mount
  useEffect(() => {
    if (mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current, {
      center: [39.5, -98.35],
      zoom: 4,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(mapRef.current);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Redraw all vessel tracks when records change
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear scrubber marker first
    if (scrubMarkerRef.current) {
      mapRef.current.removeLayer(scrubMarkerRef.current);
      scrubMarkerRef.current = null;
    }

    // Clear existing layers
    vesselLayersRef.current.forEach((layer) => {
      mapRef.current.removeLayer(layer);
    });
    vesselLayersRef.current.clear();
    vesselBoundsRef.current.clear();
    vesselRecordsRef.current.clear();
    setVessels([]);
    setVisibility({});
    setFocusedMmsi(null);
    setScrubIndex(0);

    if (!records || records.length === 0) return;

    // Group records by MMSI
    const vesselMap = new Map();
    for (const r of records) {
      if (!vesselMap.has(r.mmsi)) vesselMap.set(r.mmsi, []);
      vesselMap.get(r.mmsi).push(r);
    }

    const vesselList = [];
    const initialVisibility = {};
    let colorIndex = 0;
    let allPoints = [];

    vesselMap.forEach((vesselRecords, mmsi) => {
      const color = VESSEL_COLORS[colorIndex % VESSEL_COLORS.length];
      colorIndex++;

      // Sort by timestamp
      vesselRecords.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Store sorted records for scrubber access
      vesselRecordsRef.current.set(mmsi, vesselRecords);

      const points = vesselRecords.map((r) => [r.lat, r.lng]);
      allPoints = allPoints.concat(points);

      // Create a layer group for this vessel
      const layerGroup = L.layerGroup().addTo(mapRef.current);
      vesselLayersRef.current.set(mmsi, layerGroup);

      // Draw the track line — only if more than one point
      if (points.length > 1) {
        const trackLine = L.polyline(points, {
          color,
          weight: 2.5,
          opacity: 0.8,
        }).addTo(layerGroup);

        vesselBoundsRef.current.set(mmsi, trackLine.getBounds());
      } else {
        const pt = L.latLng(points[0][0], points[0][1]);
        vesselBoundsRef.current.set(mmsi, L.latLngBounds(pt, pt).pad(0.01));
      }

      // Start marker
      L.circleMarker(points[0], {
        radius: 7,
        color: "#16a34a",
        fillColor: "#16a34a",
        fillOpacity: 1,
      })
        .bindPopup(
          `<strong>Start</strong><br>${vesselRecords[0].vesselName || mmsi}<br>${vesselRecords[0].timestamp}`
        )
        .addTo(layerGroup);

      // End marker
      L.circleMarker(points[points.length - 1], {
        radius: 7,
        color: "#dc2626",
        fillColor: "#dc2626",
        fillOpacity: 1,
      })
        .bindPopup(
          `<strong>End</strong><br>${vesselRecords[vesselRecords.length - 1].vesselName || mmsi}<br>${vesselRecords[vesselRecords.length - 1].timestamp}`
        )
        .addTo(layerGroup);

      // Position markers (sampled)
      const step = Math.max(1, Math.floor(vesselRecords.length / 300));
      for (let i = 0; i < vesselRecords.length; i += step) {
        const r = vesselRecords[i];
        L.circleMarker([r.lat, r.lng], {
          radius: 3,
          color,
          fillColor: "#ffffff",
          fillOpacity: 0.8,
          weight: 1.5,
        })
          .bindPopup(`
            <strong>${r.vesselName || "Unknown"}</strong><br>
            MMSI: ${r.mmsi}<br>
            Time: ${r.timestamp}<br>
            Speed: ${r.speed} knots<br>
            Heading: ${r.heading}°
          `)
          .addTo(layerGroup);
      }

      vesselList.push({
        mmsi,
        name: vesselRecords[0].vesselName || "Unknown",
        color,
        count: vesselRecords.length,
        start: vesselRecords[0].timestamp,
        end: vesselRecords[vesselRecords.length - 1].timestamp,
      });

      initialVisibility[mmsi] = true;
    });

    setVessels(vesselList);
    setVisibility(initialVisibility);

    if (allPoints.length > 0) {
      mapRef.current.fitBounds(allPoints, { padding: [40, 40], maxZoom: 14 });
    }
  }, [records]);

  // Update scrubber marker position when scrubIndex or focusedMmsi changes
  useEffect(() => {
    if (!mapRef.current || !focusedMmsi) {
      // No focused vessel — remove scrubber marker if present
      if (scrubMarkerRef.current) {
        mapRef.current && mapRef.current.removeLayer(scrubMarkerRef.current);
        scrubMarkerRef.current = null;
      }
      return;
    }

    const vesselRecords = vesselRecordsRef.current.get(focusedMmsi);
    if (!vesselRecords || vesselRecords.length === 0) return;

    const idx = Math.min(scrubIndex, vesselRecords.length - 1);
    const r = vesselRecords[idx];
    const pos = [r.lat, r.lng];

    // Get this vessel's color
    const vesselMeta = vessels.find((v) => v.mmsi === focusedMmsi);
    const color = vesselMeta ? vesselMeta.color : "#1d4ed8";

    if (scrubMarkerRef.current) {
      // Move existing marker
      scrubMarkerRef.current.setLatLng(pos);
    } else {
      // Create new scrubber marker — larger, white fill, vessel color ring
      scrubMarkerRef.current = L.circleMarker(pos, {
        radius: 10,
        color: color,
        fillColor: "#ffffff",
        fillOpacity: 1,
        weight: 3,
        pane: "markerPane", // renders above the track line
      }).addTo(mapRef.current);
    }

    // Update popup content
    scrubMarkerRef.current.unbindPopup().bindPopup(`
      <strong>${r.vesselName || "Unknown"}</strong><br>
      Time: ${r.timestamp}<br>
      Speed: ${r.speed} knots<br>
      Heading: ${r.heading}°
    `);
  }, [scrubIndex, focusedMmsi, vessels]);

  // Toggle a vessel's visibility
  function toggleVessel(mmsi) {
    const layer = vesselLayersRef.current.get(mmsi);
    if (!layer || !mapRef.current) return;

    const isVisible = visibility[mmsi];

    if (isVisible) {
      mapRef.current.removeLayer(layer);
    } else {
      mapRef.current.addLayer(layer);
    }

    setVisibility((prev) => ({ ...prev, [mmsi]: !isVisible }));
  }

  // Fly to vessel + activate scrubber for that vessel
  function focusVessel(mmsi) {
    const bounds = vesselBoundsRef.current.get(mmsi);
    if (!bounds || !mapRef.current) return;

    // Make sure the vessel is visible
    const layer = vesselLayersRef.current.get(mmsi);
    if (!visibility[mmsi] && layer) {
      mapRef.current.addLayer(layer);
      setVisibility((prev) => ({ ...prev, [mmsi]: true }));
    }

    mapRef.current.fitBounds(bounds, { padding: [60, 60], animate: true });

    // Activate scrubber — reset to start of this vessel's track
    setFocusedMmsi(mmsi);
    setScrubIndex(0);
  }

  // Get current scrubber data for the focused vessel
  const focusedRecords = focusedMmsi ? (vesselRecordsRef.current.get(focusedMmsi) || []) : [];
  const focusedVesselMeta = vessels.find((v) => v.mmsi === focusedMmsi);
  const currentRecord = focusedRecords[Math.min(scrubIndex, focusedRecords.length - 1)];

  return (
    <div className="map-section">
      <h2 className="section-title">{mode === "explore" ? "Step 2" : "Step 3"} — Vessel track</h2>

      {/* Legend — one row per vessel */}
      {vessels.length > 0 && (
        <div className="map-legend">
          {vessels.map((v) => (
            <div
              key={v.mmsi}
              className={`legend-item ${!visibility[v.mmsi] ? "legend-item--hidden" : ""} ${focusedMmsi === v.mmsi ? "legend-item--focused" : ""}`}
            >
              {/* Color swatch + vessel info — click to fly to + activate scrubber */}
              <button
                className="legend-identity"
                onClick={() => focusVessel(v.mmsi)}
                title="Click to focus on this vessel and activate timeline"
              >
                <span
                  className="legend-color"
                  style={{ backgroundColor: v.color }}
                />
                <span className="legend-info">
                  <span className="legend-name">{v.name}</span>
                  <span className="legend-mmsi">MMSI {v.mmsi} · {v.count.toLocaleString()} records</span>
                </span>
              </button>

              {/* Visibility toggle */}
              <button
                className={`legend-toggle ${visibility[v.mmsi] ? "legend-toggle--on" : "legend-toggle--off"}`}
                onClick={() => toggleVessel(v.mmsi)}
                title={visibility[v.mmsi] ? "Hide vessel" : "Show vessel"}
              >
                {visibility[v.mmsi] ? "Hide" : "Show"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Timeline scrubber — shown when a vessel is focused */}
      {focusedMmsi && focusedRecords.length > 1 && (
        <div className="scrubber">
          <div className="scrubber-header">
            <span className="scrubber-label" style={{ color: focusedVesselMeta?.color }}>
              {focusedVesselMeta?.name || focusedMmsi}
            </span>
            <button
              className="scrubber-close"
              onClick={() => { setFocusedMmsi(null); setScrubIndex(0); }}
              title="Close timeline"
            >
              ✕
            </button>
          </div>
          <input
            type="range"
            className="scrubber-range"
            min={0}
            max={focusedRecords.length - 1}
            value={scrubIndex}
            onChange={(e) => setScrubIndex(Number(e.target.value))}
            style={{ "--scrubber-color": focusedVesselMeta?.color || "var(--blue)" }}
          />
          <div className="scrubber-info">
            <span className="scrubber-timestamp">{currentRecord?.timestamp || ""}</span>
            <span className="scrubber-position">
              {(scrubIndex + 1).toLocaleString()} / {focusedRecords.length.toLocaleString()}
            </span>
          </div>
          {currentRecord && (
            <div className="scrubber-details">
              <span className="scrubber-detail">Speed: {currentRecord.speed} kn</span>
              <span className="scrubber-detail">Heading: {currentRecord.heading}°</span>
              <span className="scrubber-detail">
                {currentRecord.lat?.toFixed(4)}°, {currentRecord.lng?.toFixed(4)}°
              </span>
            </div>
          )}
        </div>
      )}

      <p className="section-hint">
        Click a vessel in the legend to focus the map and activate the timeline. Click any track point for details.
      </p>

      <div ref={mapContainerRef} className="map-container" />
    </div>
  );
}
